(() => {
  const DRAFT_KEY = "repro:get-a-quote:draft:v1";
  const FILE_UPLOAD_ENDPOINT =
    "https://integrations.reprographics.co.nz/api/file-upload";
  const FILE_UPLOAD_TOKEN = "repro-graphics-store-458585968574859865879854";
  const escapeHtml = (value = "") =>
    value
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#039;");

  const extractUploadedUrls = (result) => {
    const urls = new Set();
    const inspect = (value) => {
      if (typeof value === "string") {
        if (/^https?:\/\//i.test(value)) urls.add(value);
        return;
      }
      if (Array.isArray(value)) {
        value.forEach((item) => inspect(item));
        return;
      }
      if (value && typeof value === "object") {
        Object.values(value).forEach((childValue) => inspect(childValue));
      }
    };
    inspect(result);
    return Array.from(urls);
  };

  const init = (section) => {
    if (section.dataset.initialized === "true") return;
    section.dataset.initialized = "true";

    const form = section.querySelector("form");
    const picker = section.querySelector("[data-product-picker]");
    const trigger = section.querySelector("[data-product-trigger]");
    const search = section.querySelector("[data-product-search]");
    const results = section.querySelector("[data-product-results]");
    const productValue = section.querySelector("[data-product-value]");
    const productUrl = section.querySelector("[data-product-url]");
    const productsValue = section.querySelector("[data-products-value]");
    const productUrls = section.querySelector("[data-product-urls]");
    const productLabel = section.querySelector("[data-product-label]");
    const productPlaceholder =
      section.querySelector("[data-product-placeholder]")?.innerHTML || "";
    const selectedProductsWrap = section.querySelector(
      "[data-selected-products]",
    );
    const addProductButton = section.querySelector("[data-add-product]");
    const existingOption = section.querySelector("[data-existing-customer]");
    const existingCustomerInput = existingOption?.querySelector(
      'input[type="radio"]',
    );
    const customerTypeInputs = [
      ...section.querySelectorAll('input[name="quote_customer_type"]'),
    ];
    const customerType = section.querySelector("[data-customer-type-value]");
    const orderDetails = section.querySelector(
      ".get-quote-form__order-details",
    );
    const linkToggle = section.querySelector("[data-artwork-link-toggle]");
    const upload = section.querySelector("[data-artwork-upload]");
    const linkWrap = section.querySelector("[data-artwork-link]");
    const linkInput = section.querySelector("[data-artwork-link-input]");
    const fileInput = section.querySelector("[data-artwork-file]");
    const artworkFileList = section.querySelector("[data-artwork-file-list]");
    const uploadLabel = section.querySelector("[data-upload-label]");
    const uploadLoader = section.querySelector("[data-upload-loader]");
    const artworkUrl = section.querySelector("[data-artwork-url]");
    const uploadError = section.querySelector("[data-upload-error]");
    const submitButton = form.querySelector('[type="submit"]');
    const deliveryTiming = section.querySelector("[data-delivery-timing]");
    const deliveryDateWrap = section.querySelector("[data-delivery-date]");
    const deliveryDate = deliveryDateWrap.querySelector("input");
    let searchTimer;
    let selectedProducts = [];
    let addingProduct = false;
    let selectedArtworkFiles = [];
    const artworkPreviewUrls = new Map();
    const uploadedArtworkUrls = new Map();
    let isArtworkUploading = false;
    let artworkUploadFailed = false;

    const artworkFileKey = (file) =>
      `${file.name}:${file.size}:${file.lastModified}`;

    const syncArtworkInput = () => {
      const transfer = new DataTransfer();
      selectedArtworkFiles.forEach((file) => transfer.items.add(file));
      fileInput.files = transfer.files;
    };

    const renderArtworkFiles = () => {
      artworkFileList.hidden =
        linkToggle.checked || selectedArtworkFiles.length === 0;
      artworkFileList.innerHTML = selectedArtworkFiles
        .map((file, index) => {
          const key = artworkFileKey(file);
          let preview =
            '<span class="get-quote-form__file-document" aria-hidden="true">FILE</span>';
          if (file.type.startsWith("image/")) {
            if (!artworkPreviewUrls.has(key))
              artworkPreviewUrls.set(key, URL.createObjectURL(file));
            preview = `<img src="${artworkPreviewUrls.get(key)}" alt="">`;
          }
          return `
          <div class="get-quote-form__artwork-file${uploadedArtworkUrls.has(key) ? " is-uploaded" : ""}">
            ${preview}
            <span class="get-quote-form__artwork-file-name" title="${escapeHtml(file.name)}">${escapeHtml(file.name)}</span>
            <button type="button" data-remove-artwork-file="${index}" aria-label="Remove ${escapeHtml(file.name)}">&times;</button>
          </div>`;
        })
        .join("");
      uploadLabel.textContent = selectedArtworkFiles.length
        ? "Upload more files"
        : "Upload files";

      artworkFileList
        .querySelectorAll("[data-remove-artwork-file]")
        .forEach((button) => {
          button.addEventListener("click", () => {
            const removedFile = selectedArtworkFiles.splice(
              Number(button.dataset.removeArtworkFile),
              1,
            )[0];
            const removedKey = artworkFileKey(removedFile);
            if (artworkPreviewUrls.has(removedKey)) {
              URL.revokeObjectURL(artworkPreviewUrls.get(removedKey));
              artworkPreviewUrls.delete(removedKey);
            }
            uploadedArtworkUrls.delete(removedKey);
            artworkUploadFailed = selectedArtworkFiles.some(
              (selectedFile) =>
                artworkFileKey(selectedFile) !== removedKey &&
                !uploadedArtworkUrls.has(artworkFileKey(selectedFile)),
            );
            syncArtworkInput();
            artworkUrl.value = Array.from(uploadedArtworkUrls.values()).join(
              "\n",
            );
            renderArtworkFiles();
            saveDraft();
          });
        });
    };

    const serialise = () => {
      const values = {};
      new FormData(form).forEach((value, key) => {
        if (typeof value === "string") values[key] = value;
      });
      values.artworkMode = linkToggle.checked ? "link" : "upload";
      values.selectedProducts = selectedProducts;
      return values;
    };

    const saveDraft = () =>
      sessionStorage.setItem(DRAFT_KEY, JSON.stringify(serialise()));
    const restoreDraft = () => {
      let draft;
      try {
        draft = JSON.parse(sessionStorage.getItem(DRAFT_KEY));
      } catch (_) {
        return;
      }
      if (!draft) return;
      Object.entries(draft).forEach(([name, value]) => {
        if (name === "artworkMode") return;
        const field = form.elements.namedItem(name);
        if (field && !field.value) field.value = value;
      });
      if (Array.isArray(draft.selectedProducts)) {
        selectedProducts = draft.selectedProducts.filter(
          (item) => item?.title && item?.url,
        );
        renderSelectedProducts();
      } else if (
        draft["contact[Product type]"] &&
        draft["contact[Product URL]"]
      ) {
        selectedProducts = [
          {
            title: draft["contact[Product type]"],
            url: draft["contact[Product URL]"],
          },
        ];
        renderSelectedProducts();
      }
      if (draft.artworkMode === "link") {
        linkToggle.checked = true;
        upload.hidden = true;
        linkWrap.hidden = false;
        linkInput.value = draft["contact[Artwork URL]"] || "";
      }
    };

    trigger.addEventListener("click", () => {
      addingProduct = false;
      const opening = picker.hidden;
      picker.hidden = !opening;
      trigger.setAttribute("aria-expanded", String(opening));
      if (opening) search.focus();
    });

    const syncProductHandle = (url) => {
      const selectedUrl = new URL(url, window.location.origin);
      const handleMatch = selectedUrl.pathname.match(/\/products\/([^/]+)/);
      if (!handleMatch) return;
      const currentUrl = new URL(window.location.href);
      currentUrl.searchParams.set(
        "product-handle",
        decodeURIComponent(handleMatch[1]),
      );
      window.history.replaceState({}, "", currentUrl);
    };

    const renderSelectedProducts = () => {
      productValue.value = selectedProducts[0]?.title || "";
      productUrl.value = selectedProducts[0]?.url || "";
      productsValue.value = selectedProducts
        .map((item) => item.title)
        .join("\n");
      productUrls.value = selectedProducts.map((item) => item.url).join("\n");
      productLabel.textContent =
        selectedProducts[0]?.title || "Select a product";
      trigger.classList.remove("is-invalid");
      selectedProductsWrap.hidden = selectedProducts.length === 0;
      addProductButton.hidden = selectedProducts.length === 0;
      selectedProductsWrap.innerHTML = selectedProducts
        .map(
          (item, index) => `
        <div class="get-quote-form__selected-product">
          <span>${escapeHtml(item.title)}</span>
          <button type="button" data-remove-product="${index}" aria-label="Remove ${escapeHtml(item.title)}">×</button>
        </div>`,
        )
        .join("");
      selectedProductsWrap
        .querySelectorAll("[data-remove-product]")
        .forEach((button) => {
          button.addEventListener("click", () => {
            selectedProducts.splice(Number(button.dataset.removeProduct), 1);
            renderSelectedProducts();
            if (selectedProducts.length) {
              syncProductHandle(selectedProducts[0].url);
            } else {
              const currentUrl = new URL(window.location.href);
              currentUrl.searchParams.delete("product-handle");
              window.history.replaceState({}, "", currentUrl);
            }
            saveDraft();
          });
        });
    };

    const selectProduct = (title, url, syncUrl = false, replaceAll = false) => {
      const absoluteUrl = new URL(url, window.location.origin).href;
      const productItem = { title, url: absoluteUrl };
      if (replaceAll) selectedProducts = [];
      if (addingProduct) {
        if (!selectedProducts.some((item) => item.url === absoluteUrl))
          selectedProducts.push(productItem);
      } else if (selectedProducts.length) {
        selectedProducts[0] = productItem;
      } else {
        selectedProducts.push(productItem);
      }
      addingProduct = false;
      renderSelectedProducts();
      picker.hidden = true;
      trigger.setAttribute("aria-expanded", "false");
      trigger.setCustomValidity("");

      if (syncUrl) syncProductHandle(absoluteUrl);

      saveDraft();
    };

    addProductButton.addEventListener("click", () => {
      addingProduct = true;
      picker.hidden = false;
      trigger.setAttribute("aria-expanded", "true");
      search.focus();
    });

    const bindResults = () => {
      results.querySelectorAll("[data-product-title]").forEach((button) => {
        button.addEventListener("click", () => {
          selectProduct(
            button.dataset.productTitle,
            button.dataset.productUrl,
            true,
          );
        });
      });
    };
    bindResults();

    search.addEventListener("input", () => {
      clearTimeout(searchTimer);
      const query = search.value.trim();
      if (query.length < 2) return;
      searchTimer = setTimeout(async () => {
        try {
          const response = await fetch(
            `${window.Shopify.routes.root}search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=8`,
          );
          const json = await response.json();
          const products = (json.resources?.results?.products || []).filter(
            (product) =>
              !(product.tags || []).some(
                (tag) => tag.toLowerCase() === "hide-from-listings",
              ),
          );
          results.innerHTML = products.length
            ? products
                .map(
                  (product) => `
            <button type="button" class="get-quote-form__product-result" data-product-title="${escapeHtml(product.title)}" data-product-url="${escapeHtml(product.url)}">
              ${product.featured_image?.url ? `<img src="${escapeHtml(product.featured_image.url)}" alt="" width="44" height="44">` : productPlaceholder}
              <span>${escapeHtml(product.title)}</span>
            </button>`,
                )
                .join("")
            : '<p class="get-quote-form__product-result">No products found.</p>';
          bindResults();
        } catch (_) {
          results.innerHTML =
            '<p class="get-quote-form__product-result">Products could not be loaded.</p>';
        }
      }, 250);
    });

    document.addEventListener("click", (event) => {
      if (
        !section.contains(event.target) ||
        (!picker.contains(event.target) &&
          !trigger.contains(event.target) &&
          !addProductButton.contains(event.target))
      ) {
        picker.hidden = true;
        trigger.setAttribute("aria-expanded", "false");
      }
    });

    const syncCustomerType = () => {
      const selectedType =
        customerTypeInputs.find((input) => input.checked)?.value ||
        "New customer";
      customerType.value = selectedType;
      if (orderDetails)
        orderDetails.hidden = selectedType !== "Existing customer";
      saveDraft();
    };

    customerTypeInputs.forEach((input) =>
      input.addEventListener("change", syncCustomerType),
    );

    existingCustomerInput?.addEventListener("click", (event) => {
      if (section.dataset.customerAuthenticated === "true") return;
      event.preventDefault();
      window.location.assign(section.dataset.loginUrl);
    });

    restoreDraft();

    const requestedHandle = new URLSearchParams(window.location.search).get(
      "product-handle",
    );
    const requestedQuantity = new URLSearchParams(window.location.search).get(
      "quantity",
    );
    const quantitySelect = section.querySelector("#QuoteQuantity");
    if (
      quantitySelect &&
      requestedQuantity &&
      /^\d+$/.test(requestedQuantity)
    ) {
      let quantityOption = [...quantitySelect.options].find(
        (option) => option.value === requestedQuantity,
      );
      if (!quantityOption) {
        quantityOption = new Option(
          Number(requestedQuantity).toLocaleString(),
          requestedQuantity,
        );
        quantitySelect.add(quantityOption, 1);
      }
      quantitySelect.value = requestedQuantity;
      saveDraft();
    }
    if (requestedHandle && /^[a-z0-9-]+$/i.test(requestedHandle)) {
      fetch(
        `${window.Shopify.routes.root}products/${encodeURIComponent(requestedHandle)}.js`,
      )
        .then((response) => {
          if (!response.ok) throw new Error("Product not found");
          return response.json();
        })
        .then((requestedProduct) => {
          selectProduct(
            requestedProduct.title,
            `${window.Shopify.routes.root}products/${requestedProduct.handle}`,
            false,
            true,
          );
        })
        .catch(() => {});
    }

    if (section.dataset.customerAuthenticated === "true") {
      customerType.value = "Existing customer";
      existingCustomerInput.checked = true;
    }
    syncCustomerType();

    linkToggle.addEventListener("change", () => {
      upload.hidden = linkToggle.checked;
      linkWrap.hidden = !linkToggle.checked;
      renderArtworkFiles();
      saveDraft();
    });

    const updateDeliveryDate = () => {
      const showDate = deliveryTiming.value === "Select date";
      deliveryDateWrap.hidden = !showDate;
      if (!showDate) deliveryDate.value = "";
    };
    deliveryTiming.addEventListener("change", updateDeliveryDate);
    updateDeliveryDate();

    const uploadArtworkFiles = async (files) => {
      if (!files.length) return;
      isArtworkUploading = true;
      fileInput.disabled = true;
      submitButton.disabled = true;
      uploadLoader.hidden = false;
      uploadError.hidden = true;
      uploadLabel.textContent = "Uploading…";

      try {
        const data = new FormData();
        files.forEach((file) => data.append("file", file));
        const endpoint = section.dataset.uploadUrl || FILE_UPLOAD_ENDPOINT;
        const response = await fetch(endpoint, {
          method: "POST",
          headers: { Authorization: `Bearer ${FILE_UPLOAD_TOKEN}` },
          body: data,
        });
        const result = await response.json().catch(() => ({}));
        if (!response.ok)
          throw new Error(result.message || "Artwork upload failed.");

        const urls = extractUploadedUrls(result);
        if (urls.length < files.length)
          throw new Error(
            "The upload API did not return a URL for every file.",
          );
        files.forEach((file, index) =>
          uploadedArtworkUrls.set(artworkFileKey(file), urls[index]),
        );
        artworkUrl.value = Array.from(uploadedArtworkUrls.values()).join("\n");
      } catch (error) {
        artworkUploadFailed = true;
        uploadError.textContent =
          error.message || "Artwork could not be uploaded. Please try again.";
        uploadError.hidden = false;
      } finally {
        isArtworkUploading = false;
        artworkUploadFailed = selectedArtworkFiles.some(
          (file) => !uploadedArtworkUrls.has(artworkFileKey(file)),
        );
        fileInput.disabled = false;
        submitButton.disabled = false;
        uploadLoader.hidden = true;
        renderArtworkFiles();
      }
    };

    fileInput.addEventListener("change", async () => {
      const newFiles = Array.from(fileInput.files);
      const oversizedFiles = newFiles.filter(
        (file) => file.size > 20 * 1024 * 1024,
      );
      const filesToUpload = [];
      newFiles.forEach((file) => {
        if (file.size > 20 * 1024 * 1024) return;
        const key = artworkFileKey(file);
        if (
          !selectedArtworkFiles.some(
            (selectedFile) => artworkFileKey(selectedFile) === key,
          )
        ) {
          selectedArtworkFiles.push(file);
          filesToUpload.push(file);
        }
      });
      syncArtworkInput();
      renderArtworkFiles();
      await uploadArtworkFiles(filesToUpload);
      if (oversizedFiles.length) {
        uploadError.textContent = `${oversizedFiles.map((file) => file.name).join(", ")} exceeded the 20MB file limit.`;
        uploadError.hidden = false;
      } else if (!artworkUploadFailed) {
        uploadError.hidden = true;
      }
    });
    form.addEventListener("input", saveDraft);

    submitButton.addEventListener("click", () => {
      form.classList.add("was-validated");
    });

    form.addEventListener(
      "submit",
      async (event) => {
        form.classList.add("was-validated");
        trigger.setCustomValidity("");
        trigger.classList.remove("is-invalid");

        if (linkToggle.checked) {
          artworkUrl.value = linkInput.value;
        } else if (isArtworkUploading || artworkUploadFailed) {
          event.preventDefault();
          event.stopImmediatePropagation();
          uploadError.textContent = isArtworkUploading
            ? "Please wait for the artwork upload to finish."
            : "One or more artwork files could not be uploaded. Remove them and try again.";
          uploadError.hidden = false;
        }
      },
      true,
    );

    if (form.querySelector(".get-quote-form__message--success"))
      sessionStorage.removeItem(DRAFT_KEY);
  };

  const initAll = (scope = document) =>
    scope.querySelectorAll("[data-get-quote-form]").forEach(init);
  document.addEventListener("DOMContentLoaded", () => initAll());
  document.addEventListener("shopify:section:load", (event) =>
    initAll(event.target),
  );
})();
